"use client";

import { useEffect, type CSSProperties } from "react";

/**
 * Identity-forming loader for Virtuosa: a miniature piano-roll of brand
 * violet notes falling through an atmospheric chamber. Self-contained CSS,
 * keyframes injected once into <head> to dodge Tailwind v4 tree-shake
 * (same approach as `welcome-studio-card.tsx`).
 *
 * Depth chamber: ambient violet bloom underneath, inner vignette darkening
 * the corners, top/bottom mask so notes materialize and dissolve into
 * atmosphere instead of popping. Notes carry a two-tier glow (near + halo)
 * and fall with gravity easing — the rhythm is intentionally jittered off
 * the grid so the eye never predicts the next entry.
 *
 * All notes share `--color-accent` (the brand violet) — this is the
 * Virtuosa loader, not a hand-coded teaching aid. Subtle tone variation
 * comes from the per-note `tone` field which mixes accent ↔ accent-bright.
 */

const KEYFRAMES_ID = "piano-roll-loader-keyframes";

const KEYFRAMES_CSS = `
@keyframes prl-fall {
  0%   { transform: translate3d(0, var(--prl-from), 0); }
  100% { transform: translate3d(0, var(--prl-to), 0); }
}
@keyframes prl-bloom {
  /* Keep the inline translate(-50%, -50%) (otherwise the keyframe replaces
     the whole transform stack and the bloom drifts off-centre to the
     bottom-right of the chamber). */
  0%, 100% { opacity: 0.45; transform: translate(-50%, -50%) scale(1); }
  50%      { opacity: 0.65; transform: translate(-50%, -50%) scale(1.04); }
}
@keyframes prl-label {
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  [data-prl-note], [data-prl-bloom], [data-prl-label] {
    animation: none !important;
  }
}
`;

function useInjectKeyframes() {
  useEffect(() => {
    if (document.getElementById(KEYFRAMES_ID) !== null) {
      return;
    }

    const style = document.createElement("style");
    style.id = KEYFRAMES_ID;
    style.textContent = KEYFRAMES_CSS;
    document.head.appendChild(style);
  }, []);
}

interface FallingNote {
  /** Lane index (0..lanes-1). */
  lane: number;
  /** `base` = `--color-accent`, `bright` = `--color-accent-bright`. Subtle
   *  micro-variation, all in the same brand violet family. */
  tone: "base" | "bright";
  /** Loop duration in seconds. Slightly varied to avoid lockstep. */
  duration: number;
  /** Negative delay so the first cycle is staggered without an empty start. */
  delay: number;
  /** Note length, multiplier of `baseNoteH`. */
  length: number;
}

interface SizeSpec {
  width: number;
  height: number;
  noteW: number;
  baseNoteH: number;
  radius: number;
}

const SIZES: Record<"sm" | "md" | "lg", SizeSpec> = {
  sm: { width: 72, height: 56, noteW: 6, baseNoteH: 12, radius: 6 },
  md: { width: 104, height: 80, noteW: 8, baseNoteH: 18, radius: 8 },
  lg: { width: 144, height: 112, noteW: 10, baseNoteH: 24, radius: 10 },
};

// Durations and delays jittered ±90ms off the grid so the eye stops
// predicting the next note. Same overall density (~one note every 320ms),
// but the rhythm reads as musical rather than metronomic. Tones alternate
// loosely between `base` and `bright` for micro-variation in the same
// violet family.
const NOTES: FallingNote[] = [
  { lane: 0, tone: "base",   duration: 1.55, delay: -0.08, length: 1.4 },
  { lane: 1, tone: "bright", duration: 1.7,  delay: -0.49, length: 0.85 },
  { lane: 2, tone: "base",   duration: 1.5,  delay: -0.81, length: 1.7 },
  { lane: 3, tone: "bright", duration: 1.65, delay: -1.27, length: 1.0 },
  { lane: 4, tone: "base",   duration: 1.6,  delay: -1.52, length: 1.25 },
];

const LANES = 5;

// Gravity easing — notes feel dropped, not conveyor-belt'd.
const FALL_EASING = "cubic-bezier(0.32, 0, 0.67, 0)";

export interface PianoRollLoaderProps {
  size?: "sm" | "md" | "lg";
  /**
   * `chamber` (default): notes fall inside a bordered, vignette-lit chamber.
   * Use inside dense UI as an inline indicator.
   *
   * `bare`: no chamber, no border — just the ambient bloom and the falling
   * notes with a soft top/bottom dissolve. Designed to sit on a splash
   * screen or any plain `--color-bg-deep` surface.
   */
  variant?: "chamber" | "bare";
  /** Optional label rendered below, pulses gently. */
  label?: string;
  className?: string;
}

export function PianoRollLoader({
  size = "md",
  variant = "chamber",
  label,
  className,
}: PianoRollLoaderProps) {
  useInjectKeyframes();

  const dim = SIZES[size];
  const laneW = dim.width / LANES;
  // The bare variant gets a wider, softer halo since it's the only
  // atmospheric anchor left without the chamber walls.
  const bloomSize = Math.round(dim.width * (variant === "bare" ? 1.9 : 1.35));
  const bloomBlur = variant === "bare" ? 28 : 18;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label ?? "Chargement"}
      className={`inline-flex flex-col items-center gap-2.5 ${className ?? ""}`}
    >
      <div
        style={{
          position: "relative",
          width: dim.width,
          height: dim.height,
          isolation: "isolate",
        }}
      >
        {/* Ambient violet bloom — sits behind, bleeds out. */}
        <div
          data-prl-bloom
          aria-hidden
          style={{
            position: "absolute",
            left: "50%",
            top: "55%",
            width: bloomSize,
            height: bloomSize,
            transform: "translate(-50%, -50%)",
            borderRadius: "50%",
            background:
              "radial-gradient(ellipse at center, var(--color-violet-glow) 0%, transparent 60%)",
            filter: `blur(${bloomBlur}px)`,
            zIndex: -1,
            pointerEvents: "none",
            animation: "prl-bloom 3.2s ease-in-out infinite",
          }}
        />

        <ChamberContent variant={variant} dim={dim} laneW={laneW} />
      </div>

      {label !== undefined && (
        <span
          data-prl-label
          className="text-[11px] font-medium tracking-wide text-text-secondary"
          style={{ animation: "prl-label 1.6s ease-in-out infinite" }}
        >
          {label}
        </span>
      )}
    </div>
  );
}

interface ChamberContentProps {
  variant: "chamber" | "bare";
  dim: SizeSpec;
  laneW: number;
}

function ChamberContent({ variant, dim, laneW }: ChamberContentProps) {
  const isBare = variant === "bare";

  // Shared wrapper styles — chamber has walls/floor, bare has neither.
  const wrapperStyle: CSSProperties = {
    position: "relative",
    width: "100%",
    height: "100%",
    borderRadius: isBare ? 0 : dim.radius,
    overflow: "hidden",
    background: isBare
      ? "transparent"
      : "linear-gradient(180deg, var(--color-bg-elevated) 0%, var(--color-bg-deep) 100%)",
    border: isBare ? "none" : "1px solid var(--color-border-subtle)",
    boxShadow: isBare
      ? "none"
      : "inset 0 1px 0 rgba(255,255,255,0.04), 0 4px 16px rgba(0,0,0,0.4)",
  };

  return (
    <div style={wrapperStyle}>
      {/* Lane dividers — chamber-only, would be too clinical on a splash. */}
      {!isBare && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `repeating-linear-gradient(
              to right,
              transparent 0,
              transparent ${laneW - 1}px,
              rgba(255,255,255,0.04) ${laneW - 1}px,
              rgba(255,255,255,0.04) ${laneW}px
            )`,
            maskImage:
              "linear-gradient(180deg, transparent 0%, #000 30%, #000 100%)",
          }}
        />
      )}

      {/* Falling notes layer — masked top & bottom so notes materialize
          and dissolve into the atmosphere. The bare variant uses a deeper
          mask since there are no chamber walls to bound them. */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          maskImage: isBare
            ? "linear-gradient(180deg, transparent 0%, #000 28%, #000 78%, transparent 100%)"
            : "linear-gradient(180deg, transparent 0%, #000 18%, #000 88%, transparent 100%)",
          WebkitMaskImage: isBare
            ? "linear-gradient(180deg, transparent 0%, #000 28%, #000 78%, transparent 100%)"
            : "linear-gradient(180deg, transparent 0%, #000 18%, #000 88%, transparent 100%)",
        }}
      >
        {NOTES.map((note, i) => {
          const noteH = dim.baseNoteH * note.length;
          const left = note.lane * laneW + (laneW - dim.noteW) / 2;
          const color =
            note.tone === "bright"
              ? "var(--color-accent-bright)"
              : "var(--color-accent)";

          const style: CSSProperties = {
            position: "absolute",
            left,
            top: 0,
            width: dim.noteW,
            height: noteH,
            borderRadius: dim.noteW,
            background: `linear-gradient(180deg,
              color-mix(in srgb, ${color} 70%, transparent) 0%,
              ${color} 60%,
              ${color} 100%)`,
            boxShadow: `
              0 0 10px color-mix(in srgb, ${color} 55%, transparent),
              0 0 22px color-mix(in srgb, ${color} 22%, transparent)
            `,
            willChange: "transform",
            animation: `prl-fall ${note.duration}s ${FALL_EASING} ${note.delay}s infinite`,
            ["--prl-from" as string]: `${-noteH}px`,
            ["--prl-to" as string]: `${dim.height + noteH * 0.2}px`,
          };

          return <span key={i} data-prl-note style={style} />;
        })}
      </div>

      {/* Inner vignette — chamber-only, the bare variant relies on its
          own mask + bloom for depth. */}
      {!isBare && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "radial-gradient(ellipse at 50% 45%, transparent 35%, rgba(0,0,0,0.45) 100%)",
          }}
        />
      )}
    </div>
  );
}
